import Vue from 'vue';
import _ from 'lodash';
import path from 'path';
import { mapGetters } from 'vuex';
import { guid } from '@/mixins/guid';
import { siteBuilder } from '@/mixins/site-builder';
import dropsLoader from '@/mixins/components/drops-loader';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import moment from 'moment-timezone';
import { SITE_ASSET_FOLDERS } from '@/constants';

export default Vue.component('creator-options', {
  name: 'creator-options',
  template: require('./template.html'),
  components: {
    dropsLoader,
    uploadProgress,
  },
  computed: {
    ...mapGetters([
      'currentSite',
      'currentPage',
      'currentUser',
      'identityId',
      'boilerplates',
      'publishingBoilerplate',
    ]),
    filteredCategories() {
      return this.boilerCategories.sort(); // eslint-disable-line
    },
  },
  mixins: [guid, siteBuilder, uploader],
  data() {
    return {
      loading: false,
      errorMessage: false,
      optionHint: false,
      exportFile: null,
      boilerplatename: '',
      newPlateId: null,
      boilerthumb: null,
      boilerthumbUrl: '',
      activeOption: null,
      boilerCategories: [
        'general',
        'blog',
        'profiles',
        'music',
        'entertainment',
        'business',
        'startup',
        'restaurant',
        'real estate',
        'church',
        'mission',
        'non-profit',
        'technology',
        'sports',
        'photography',
      ],
      selectedCategory: 'general',
      warnColor: false,
    };
  },
  watch: {
    modal(o) {
      this.$store.dispatch('setGlobalOpenModal', o);
    },
  },
  props: ['modal'],
  methods: {
    openUpload() {
      this.$refs.uploadFile.click();
    },
    uploadThumbnail(e) {
      this.boilerthumb = [...e.target.files];
      this.$validator.validate('boilerthumb', this.boilerthumb).then(async (result) => {
        if (result) {
          let newFile = [...e.target.files];
          if (newFile.length > 0) {
            newFile = newFile[0];
            // clean up the file name for s3
            let fileName = path.basename(newFile.name);
            fileName = `drz_boilerplate_thumbnail_${_.trim(fileName)}`;
            fileName = _.replace(fileName, /\s+/g, '_');

            const boilerplate = _.cloneDeep(this.currentSite.boilerplate);
            boilerplate.name = boilerplate.name.toLowerCase();
            boilerplate.name = boilerplate.name.replace(/[^a-zA-Z0-9]/g, '-');

            try {
              // send off to our s3 upload mixin
              const thumb = await this.uploadFile(
                {
                  file: `thumbnail/${fileName}`,
                  body: newFile,
                },
                { boilerplate },
              );
              this.boilerthumbUrl = thumb.Location;
            } catch (err) {
              if (!err.message.match(/aborted/gi)) {
                this.$log.error(err);
                await this._reportError({
                  error: err,
                  fn: 'uploadThumbnail - creator options',
                });
              }
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          e.target.value = null;
        }
      });
    },
    async publishBoilerplate() {
      this.$validator.validate('boilerthumb', this.boilerthumb).then(async (result) => {
        if (result) {
          this.loading = true;
          this.errorMessage = false;
          let type = 'success';
          let message = 'Boilerplate published successfully!';
          try {
            if (this.currentSite.drafts.length > 0) {
              throw new Error('Drafts are no currently allowed when submitting boilerplates');
            }
            const boilerplate = _.cloneDeep(this.currentSite.boilerplate);
            boilerplate.name = boilerplate.name.toLowerCase();
            boilerplate.name = boilerplate.name.replace(/[^a-zA-Z0-9]/g, '-');
            this.$store.dispatch('setPublishBoilerplate', boilerplate.name);
            await this.$store.dispatch('setCurrentPage', 'loading');
            // publish and copy all assets
            const response = await this._SEND([{
              name: 'publishBoilerplate',
              payload: {
                site: this.currentSite,
                assetFolders: SITE_ASSET_FOLDERS,
                plateId: boilerplate._id,
                thumb: this.boilerthumbUrl,
                name: boilerplate.name,
              },
            }]);
            this.$log.debug('successfully updated boilerplate: ', response);
            await this.replaceLocalAssets(response);
            const homepage = this.currentSite.pages.find(page => page.isHomepage);
            this.$store.dispatch('setOriginalPage', homepage);
            await this.$store.dispatch('setCurrentPage', homepage);
            // generate entire site
            await this.callBuilder({
              publish: 'site',
              publishingBoilerplate: boilerplate.name,
              site: {
                _id: this.currentSite._id,
              },
            });
            this.$log.debug('successfully published boilerplate: ', { boilerplate });
            // reset bp back for generator hbs helpers
            this.$store.dispatch('setPublishBoilerplate', false);
            this.boilerthumb = null;
            this.boilerthumbUrl = '';
          } catch (e) {
            this.$log.error('error: ', e);
            this.$log.error('You may just need to try again as local static files werent there.');
            type = 'error';
            message = e.message || e;
          } finally {
            this.loading = false;
            this.$emit('toggle-modal');
            const homepage = this.currentSite.pages.find(page => page.isHomepage);
            this.$store.dispatch('setOriginalPage', homepage);
            await this.$store.dispatch('setCurrentPage', homepage);
            setTimeout(() => {
              this.$store.dispatch('addNotification', {
                id: this.guid(),
                type,
                message,
              });
            }, 500);
          }
        }
      });
    },
    async replaceLocalAssets(params) {
      // replacing the current site with the updated boilerplate assets
      // only so that we can generate the site with them and publish that
      // to the boilerplate folder/bucket.
      const site = this.currentSite;
      this.$store.dispatch('runStateFn', {
        fn() {
          site.pages = params.updatedData.pages;
          site.drafts = params.updatedData.drafts;
          site.templates = params.updatedData.templates;
          site.recycled = params.updatedData.recycled;
          site.siteMap = params.updatedData.siteMap;
          site.meta = params.newSite.meta;
          site.favicon = params.newSite.favicon;
        },
      });
    },
    async submitBoilerplate() {
      // make sure name is not taken
      this.errorMessage = false;
      await this.loadBoilerPlates();
      const nameList = this.boilerplates.map(b => b.name);
      if (nameList.includes(this.boilerplatename)) {
        this.errorMessage = 'A boilerplate with that name already exists. Please use another.';
        return;
      }
      this.$validator.validate('boilerplatename', this.boilerplatename).then(async (result) => {
        if (result) {
          this.loading = true;
          let message =
            'Your boilerplate has been submitted! We will review this and be in touch. Thanks!';
          let type = 'success';
          try {
            const site = _.cloneDeep(this.currentSite);
            site.originFolder = this.identityId;
            await this._SEND([{
              name: 'submitBoilerplate',
              payload: {
                site,
                boilerplatename: this.boilerplatename,
                category: this.selectedCategory,
                createdOn: new Date().toISOString(),
              },
              webhook: {
                internal: true,
                type: 'submit-boilerplate',
                payload: {
                  user: this.currentUser,
                },
              },
            }]);
            this.$log.debug('successfully added boilerplate: ', this.boilerplatename);
            this.$emit('toggle-modal');
          } catch (e) {
            this.$log.error('error: ', e);
            type = 'error';
            message = `${e.message || e}. This has been reported.`;
            await this._reportError({
              error: e,
              fn: 'submitBoilerplate',
            });
          } finally {
            this.loading = false;
            this.$store.dispatch('addNotification', {
              id: this.guid(),
              type,
              message,
            });
          }
        }
      });
    },
    async loadBoilerPlates() {
      this.loading = true;
      try {
        const boilerplates = await this._SEND([{
          name: 'getBoilerplates',
        }]);
        this.$log.debug('successfully added boilerplates: ', boilerplates);
        let list = _.filter(boilerplates.Items, plate => plate.status !== 'pending');
        if (this._dev) {
          list = boilerplates.Items;
        }
        this.$store.dispatch('setBoilerplates', list);
      } catch (e) {
        this.$log.error('error: ', e);
      } finally {
        this.loading = false;
      }
    },
    nameCheck(e) {
      let val = e.currentTarget.value;
      val = val.replace(/[^a-zA-Z0-9|\s]/g, '-');
      this.boilerplatename = val;
    },
    exportSite() {
      const site = _.cloneDeep(this.currentSite);
      site.originFolder = this.identityId;
      const data = new Blob([JSON.stringify(site)], {
        type: 'application/json',
      });
      if (this.exportFile !== null) {
        window.URL.revokeObjectURL(this.exportFile);
      }
      const fileName = `${site.alias}-${moment(new Date()).format('MM-DD-YY_hh-mm-ss_a')}`;
      this.exportFile = window.URL.createObjectURL(data);
      const $btn = this.$refs.downloadJSON;
      $btn.setAttribute('href', this.exportFile);
      $btn.setAttribute('download', `${fileName}.json`);
      $btn.click();
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/creator-options/index.js